/**
 * Tell the runtime which displays exist, and which one it is actually looking
 * at.
 *
 * A prompt attaches only the display the cursor is on, and the header in front
 * of it says "screen=1 of 1". With two monitors connected that reads as "your
 * desktop is one screen", so a request to find something on the *other* screen
 * is answered from the wrong image: observed with the desktop icons on one
 * display and the cursor on another, where the runtime described windows on the
 * covered screen and never suspected the icons were elsewhere. It was not
 * ignoring the second display - it was never sent it, and nothing told it there
 * was one.
 *
 * This states the roster and stops there. Position names ("left", "top") are
 * resolved by the app in a desktop_interact state look, so naming them here as
 * well would be a second copy of that logic, free to drift; the token names ids
 * and points at the tool that turns them into positions.
 */
import { getAllWindows } from "@tauri-apps/api/window";
import { isTauri } from "@/lib/is-tauri";

const DISPLAY_WINDOW_PREFIX = "display-";

/** The app builds exactly one overlay window per display, labelled
 *  `display-<native id>`, which makes the window list the roster. */
export function displayIdsFromWindowLabels(labels: string[]): number[] {
  const ids = labels.flatMap((label) => {
    if (!label.startsWith(DISPLAY_WINDOW_PREFIX)) return [];
    const id = Number(label.slice(DISPLAY_WINDOW_PREFIX.length));
    return Number.isInteger(id) && id > 0 ? [id] : [];
  });
  return [...new Set(ids)].sort((a, b) => a - b);
}

export function buildDisplayRosterToken(
  attachedDisplayIds: number[],
  allDisplayIds: number[],
): string | null {
  const attached = new Set(attachedDisplayIds);
  const missing = allDisplayIds.filter((id) => !attached.has(id));
  // Nothing to correct: the runtime can see every display it has.
  if (missing.length === 0 || attached.size === 0) return null;
  const shown = [...attached].join(", ");
  return (
    `[DISPLAYS] This prompt shows only display ${shown}. ` +
    `Your desktop also has display ${missing.join(", ")}, not attached here. ` +
    "If the request is about something you cannot find in the attached image, " +
    "it may be on one of those: call desktop_interact " +
    "look:{what:'state', include:['screens']} to resolve them by position, " +
    "then look:{what:'screenshot', area} on that screen's frame, " +
    "and aim with capture_point or capture_rect against the returned " +
    "capture_id. Do not answer from the attached image alone when the target " +
    "is not in it. [/DISPLAYS]"
  );
}

/** Gathers the roster from the running app. Best-effort: a failure means the
 *  prompt goes out exactly as it did before, without the roster line. */
export async function displayRosterToken(
  attachedDisplayIds: number[],
): Promise<string | null> {
  if (!isTauri() || attachedDisplayIds.length === 0) return null;
  try {
    const windows = await getAllWindows();
    const ids = displayIdsFromWindowLabels(windows.map((w) => w.label));
    return buildDisplayRosterToken(attachedDisplayIds, ids);
  } catch {
    return null;
  }
}
